/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { n, groupVariantsByColor, getVariantColorHex } from "../../theme1/utils/theme1Helpers";
import { formatNumber } from "utils/formatNumber";
import { getCardGalleryImages } from "../../../utils/productMainImages";
import MainImageCarousel from "../../components/MainImageCarousel";
import Swal from "sweetalert2";

export default function ProductModal({ selected, setSelected, theme, handleAddToCart, products }) {
  const [selectedColorHex, setSelectedColorHex] = useState(null);
  const [selectedSizeLabel, setSelectedSizeLabel] = useState(null);
  const [quantity, setQuantity] = useState(1);

  // 1. Find Product & Group Variants
  const product = useMemo(() => {
    if (!selected || !products?.length) return null;
    return products.find(p => Number(p.id) === Number(selected.productId || selected.id));
  }, [selected, products]);

  const colorGroups = useMemo(() => groupVariantsByColor(product?.variants), [product]);
  const uniqueColors = useMemo(() => Object.values(colorGroups), [colorGroups]);
  const displayColors = useMemo(() => uniqueColors.filter((c) => c.hasColor), [uniqueColors]);

  const getSizesFromGroup = (group) => {
    const allSizes = [];
    group?.variants?.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
    });
    return allSizes;
  };

  // 2. Initial Selection
  useEffect(() => {
    if (selected && product && uniqueColors.length > 0) {
      // Find initial color from the 'selected' prop if possible
      const initialVariant = product.variants.find(v => Number(v.variantId) === Number(selected.variantId)) || product.variants[0];
      const desiredHex = getVariantColorHex(initialVariant) || uniqueColors[0]?.hex;
      const preferredGroup = uniqueColors.find(c => c.hex === desiredHex);
      const hasStockInPreferred = getSizesFromGroup(preferredGroup).some(s => s.stock > 0);
      const firstInStockColor = uniqueColors.find(c => getSizesFromGroup(c).some(s => s.stock > 0));
      const initialHex = (hasStockInPreferred ? preferredGroup : (firstInStockColor || preferredGroup || uniqueColors[0]))?.hex || "";

      setSelectedColorHex(initialHex);

      const group = colorGroups[initialHex];
      const allSizes = getSizesFromGroup(group);

      const initialSizeLabel = selected.size || "";
      const matchedSize = allSizes.find(s => s.label === initialSizeLabel && s.stock > 0) || allSizes.find(s => s.stock > 0) || allSizes[0];

      if (matchedSize) setSelectedSizeLabel(matchedSize.label);
      setQuantity(1);
    }
  }, [selected, product, uniqueColors, colorGroups]);

  // 3. Derived State
  const activeColorGroup = colorGroups[selectedColorHex] || uniqueColors[0];

  const availableSizes = useMemo(() => {
    if (!activeColorGroup) return [];
    const sizeMap = new Map();
    activeColorGroup.variants.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => {
        const label = s.label || s.size;
        let stock = n(s.stock || s.stockQty);
        if (stock <= 0 && n(v.stockQty) > 0) stock = n(v.stockQty);
        if (!sizeMap.has(label)) {
          sizeMap.set(label, { label, stock, variant: v });
        } else {
          const existing = sizeMap.get(label);
          existing.stock += stock;
        }
      });
    });
    return Array.from(sizeMap.values());
  }, [activeColorGroup]);

  const activeSizeObj = useMemo(() => {
    return availableSizes.find(s => s.label === selectedSizeLabel) || availableSizes[0];
  }, [availableSizes, selectedSizeLabel]);

  const activeVariant = activeSizeObj?.variant || activeColorGroup?.variants[0];
  const galleryImages = useMemo(
    () => getCardGalleryImages(product, activeVariant?.imageUrl),
    [product, activeVariant]
  );

  const priceDetails = useMemo(() => {
    if (!activeVariant || !product) return { base: 0, discount: 0, final: 0 };
    const basePrice = Number(activeVariant.price || activeVariant.basePrice || 0);
    const preCalculatedDiscount = Number(activeVariant.discount || 0);
    const preCalculatedFinal = Number(activeVariant.finalPrice || (basePrice - preCalculatedDiscount));
    const dValue = Number(product.discountValue || 0);
    const dType = Number(product.discountType || 0);
    let discountAmount = preCalculatedDiscount;
    let finalPrice = preCalculatedFinal;
    if (discountAmount === 0 && dValue > 0) {
      if (dType === 1) discountAmount = (basePrice * dValue) / 100;
      else discountAmount = dValue;
      finalPrice = basePrice - discountAmount;
    }
    return { base: basePrice, discount: discountAmount, final: Math.max(0, finalPrice) };
  }, [activeVariant, product]);

  const isProductOutOfStock = useMemo(() => {
    if (!product?.variants?.length) return true;
    return !product.variants.some(v => {
      const vSizes = v.options || v.sizes || [];
      if (vSizes.length === 0) return n(v.stockQty) > 0;
      const hasSizeStock = vSizes.some(s => n(s.stock || s.stockQty) > 0);
      return hasSizeStock || n(v.stockQty) > 0;
    });
  }, [product]);

  if (!selected || !product || !activeVariant) return null;

  const handleColorChange = (hex) => {
    setSelectedColorHex(hex);
    const group = colorGroups[hex];
    const allSizes = getSizesFromGroup(group);
    const firstAvailable = allSizes.find(o => o.stock > 0) || allSizes[0];
    if (firstAvailable) setSelectedSizeLabel(firstAvailable.label);
    setQuantity(1);
  };

  const currentStock = n(activeSizeObj?.stock || activeVariant?.stockQty || 0);
  const isOutOfStock = currentStock <= 0;
  const lowStockValue = Number(activeVariant?.lowStockValue || 5);
  const showOutOfStockAlert = () => {
    Swal.fire({ icon: "info", title: "انتهى من المخزون", text: "هذا الخيار غير متوفر حالياً" });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[25000] p-0" onClick={() => setSelected(null)}>
      <div className="bg-[#0f0f0f] border border-white/10 max-w-none w-screen h-screen max-h-none rounded-none overflow-hidden flex flex-col md:flex-row shadow-2xl animate-in fade-in zoom-in duration-300 relative" onClick={e => e.stopPropagation()}>

        {/* Floating Close Button for Mobile */}
        <button onClick={() => setSelected(null)} className="absolute top-6 left-6 z-10 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 md:hidden transition-all text-2xl">×</button>

        {/* Image Section - Premium Look */}
        <div className="w-full md:w-1/2 h-64 md:h-auto bg-white/5 flex items-center justify-center relative p-10 overflow-hidden">
          <MainImageCarousel
            images={galleryImages}
            alt={product.name}
            variant="dark"
            imgClassName="max-w-full max-h-full object-contain filter drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-all duration-700 hover:scale-110"
          />
          {priceDetails.discount > 0 && (
            <div className="absolute top-6 right-6 bg-red-600 text-white px-4 py-1.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-red-900/40">
              - {product.discountType === 1 ? `${product.discountValue}%` : 'SALE'}
            </div>
          )}
          {isProductOutOfStock && (
            <div className="absolute inset-0 bg-black/45 flex items-center justify-center z-20">
              <span className="px-3 py-1 rounded-lg bg-white/90 text-gray-800 text-[10px] font-black">انتهى المخزون</span>
            </div>
          )}
        </div>

        {/* Details Section */}
        <div className="p-8 md:w-1/2 flex flex-col justify-between space-y-6 overflow-y-auto flex-1 custom-scrollbar text-right" dir="rtl">
          <div className="space-y-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-bold mb-1"># {product.itemCode || product.id}</p>
                {(product.category || product.subCategory) && (
                  <div className="flex items-center gap-2 mb-1">
                    {product.category && (
                      <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-white/10" style={{ color: theme.primary }}>
                        {product.category}
                      </span>
                    )}
                    {product.subCategory && (
                      <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">
                        {product.subCategory}
                      </span>
                    )}
                  </div>
                )}
                <span className="text-xs font-black uppercase tracking-widest" style={{ color: theme.primary }}>{product.brand || "خيارات متعددة متاحة"}</span>
                <h4 className="text-2xl font-black text-white leading-tight mt-1">{product.name}</h4>
              </div>
              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {product.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-[10px] font-bold"
                      style={{ background: `${theme.primary}22`, color: theme.primary, border: `1px solid ${theme.primary}44` }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              <button onClick={() => setSelected(null)} className="text-white/20 hover:text-white transition-colors text-2xl font-light hidden md:block">✕</button>
            </div>

            <p className="text-white/40 text-[13px] leading-relaxed line-clamp-3">{product.description}</p>

            <div className="flex items-baseline gap-3 pt-2">
              <div className="text-3xl font-black" style={{ color: theme.primary }}>
                {formatNumber(priceDetails.final)} <span className="text-[10px] opacity-60 font-normal">د.ع</span>
              </div>
              {priceDetails.discount > 0 && (
                <div className="text-sm text-white/20 line-through font-bold">
                  {formatNumber(priceDetails.base)}
                </div>
              )}
            </div>

            {/* Stock Info Status */}
            <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest">
              <div className="flex items-center gap-2 text-white/60 bg-white/5 px-3 py-1.5 rounded-full border border-white/5">
                <div className={`w-2 h-2 rounded-full ${isOutOfStock ? 'bg-red-500 shadow-[0_0_8px_#ef4444]' : currentStock <= lowStockValue ? 'bg-amber-500 shadow-[0_0_8px_#f59e0b]' : 'bg-green-500 shadow-[0_0_8px_#22c55e]'}`} />
                {isOutOfStock ? "نفذت الكمية" : currentStock <= lowStockValue ? "كمية محدودة" : "متوفر حالياً"}
              </div>
              {!isOutOfStock && (
                <div className="text-white/20">{currentStock} قطعة متوفرة</div>
              )}
            </div>

            {/* Selection Options */}
            <div className="space-y-5 border-t border-white/5 pt-5">
              {/* Colors - Unique ONLY */}
              {displayColors.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[10px] text-white/40 font-black uppercase tracking-[0.2em]">اختيار اللون ({activeColorGroup?.name})</p>
                  <div className="flex flex-wrap gap-4">
                    {displayColors.map((c) => {
                      const isActive = selectedColorHex === c.hex;
                      return (
                        <button
                          key={c.hex}
                          onClick={() => handleColorChange(c.hex)}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${isActive ? 'scale-125 border-white shadow-[0_0_20px_rgba(255,255,255,0.4)]' : 'border-white/30 hover:border-white/70 hover:scale-110 shadow-lg'}`}
                          style={{ background: c.hex }}
                          title={c.name}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Sizes - Grouped */}
              {availableSizes.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[10px] text-white/40 font-black uppercase tracking-[0.2em]">المقاس المتاح</p>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map((opt, idx) => {
                      const isSelected = selectedSizeLabel === opt.label;
                      const out = opt.stock <= 0;
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            if (out) {
                              showOutOfStockAlert();
                              return;
                            }
                            setSelectedSizeLabel(opt.label);
                          }}
                          className={`
                            px-5 py-2.5 rounded-xl border text-[11px] font-black transition-all
                            ${isSelected
                              ? 'text-black'
                              : out ? 'bg-white/5 border-transparent text-white/10 cursor-not-allowed' : 'bg-white/5 border-white/10 text-white/60 hover:border-white/30 hover:text-white'}
                          `}
                          style={{
                            backgroundColor: isSelected ? theme.primary : undefined,
                            borderColor: isSelected ? theme.primary : undefined
                          }}
                        >
                          {opt.label}
                          <span className="block text-[8px] opacity-40 font-medium">{opt.stock} قطعة</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Footer */}
          <div className="pt-6 border-t border-white/5 space-y-5">
            {/* Quantity Selector */}
            {!isOutOfStock && (
              <div className="flex items-center justify-between bg-white/5 rounded-2xl p-2 border border-white/5">
                <span className="text-[10px] text-white/40 font-black px-4 uppercase tracking-[0.2em]">الكمية</span>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => {
                      if (quantity < currentStock) setQuantity(q => q + 1);
                      else Swal.fire({ title: "عفواً", text: "وصلت للحد الأقصى للمخزون", icon: "warning", timer: 1500 });
                    }}
                    className="w-10 h-10 flex items-center justify-center text-white/30 hover:text-white hover:bg-white/10 rounded-xl transition-all font-bold text-xl"
                  >+</button>
                  <span className="text-lg font-black text-white min-w-[20px] text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-10 h-10 flex items-center justify-center text-white/30 hover:text-white hover:bg-white/10 rounded-xl transition-all font-bold text-xl"
                  >−</button>
                </div>
              </div>
            )}

            {/* Add to Cart Button */}
            <button
              disabled={isOutOfStock}
              onClick={() => {
                if (isOutOfStock) {
                  showOutOfStockAlert();
                  return;
                }
                handleAddToCart({
                  variantId: Number(activeVariant.variantId),
                  price: Number(priceDetails.final),
                  basePrice: Number(priceDetails.base),
                  discount: Number(priceDetails.discount),
                  color: getVariantColorHex(activeVariant),
                  colorName: getVariantColorHex(activeVariant),
                  size: String(selectedSizeLabel || ""),
                  imageUrl: activeVariant.imageUrl || product.mainImage,
                  name: product.name,
                  stockQty: currentStock
                }, quantity);
                setSelected(null);
              }}
              className={`
                w-full py-5 rounded-[1.8rem] font-black text-sm uppercase tracking-[0.2em] 
                transition-all duration-300 transform active:scale-[0.97] shadow-2xl
                ${isOutOfStock ? 'grayscale opacity-20 bg-white/10 cursor-not-allowed' : 'hover:brightness-110'}
              `}
              style={{
                backgroundColor: isOutOfStock ? undefined : theme.primary,
                color: isOutOfStock ? '#fff' : "#000",
                boxShadow: isOutOfStock ? 'none' : `0 15px 40px ${theme.primary}40`
              }}
            >
              {isOutOfStock ? "نفذت الكمية" : "إضافة للسلة"}
            </button>
          </div>
        </div>
      </div>
      <style jsx="true">{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
      `}</style>
    </div>
  );
}
